<template>
  <div class="image-edit-page">
    <!-- 顶部标题栏 -->
    <div class="header">
      <h1 class="page-title">AI 图片编辑</h1>
      <div class="header-actions">
        <button class="action-btn">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="2.5"/>
            <circle cx="6" cy="12" r="2.5"/>
            <circle cx="18" cy="12" r="2.5"/>
          </svg>
        </button>
        <button class="action-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 切换标签 -->
    <div class="tab-container">
      <button class="tab-btn" :class="{ active: currentTab === 'image' }" @click="currentTab = 'image'">
        图片编辑
      </button>
      <button class="tab-btn" :class="{ active: currentTab === 'video' }" @click="currentTab = 'video'">
        视频生成
      </button>
    </div>

    <!-- 图片生成内容 -->
    <div class="content-section" v-if="currentTab === 'image'">
      <!-- 上传参考图 -->
      <div class="upload-section">
        <div class="section-title">上传参考图</div>
        <div class="upload-grid">
          <div 
            v-for="(img, index) in uploadedImages" 
            :key="index" 
            class="uploaded-image-wrapper"
          >
            <img :src="img" alt="参考图" />
            <button class="remove-img-btn" @click="removeImage(index)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          
          <div 
            v-if="uploadedImages.length < 3" 
            class="upload-box" 
            @click="triggerUpload"
          >
            <input 
              ref="fileInput" 
              type="file" 
              accept="image/*" 
              @change="handleFileSelect" 
              style="display: none" 
              multiple
            />
            <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- 画面描述 -->
      <div class="prompt-section">
        <div class="section-title">画面描述</div>
        <textarea 
          class="prompt-input" 
          v-model="prompt" 
          placeholder="描述你想要生成的画面，例如：一个穿着汉服的女孩在樱花树下，阳光透过花瓣洒在她的脸上..."
          rows="4"
        ></textarea>
      </div>

      <!-- 模型选择 -->
      <div class="model-section">
        <div class="section-title">选择模型</div>
        <div class="model-grid">
          <div class="model-card" :class="{ active: selectedModel === 'nano' }" @click="selectedModel = 'nano'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="12" r="4" fill="currentColor"/>
              </svg>
            </div>
            <div class="model-name">Nano</div>
            <div class="model-desc">基础模型</div>
          </div>
          
          <div class="model-card" :class="{ active: selectedModel === 'nano-pro' }" @click="selectedModel = 'nano-pro'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="model-name">Nano Pro</div>
            <div class="model-desc">精准中文理解输出 &4k</div>
          </div>

          <div class="model-card" :class="{ active: selectedModel === 'gpt-image-2' }" @click="selectedModel = 'gpt-image-2'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" stroke-width="2"/>
                <path d="M8 12l3 3 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="model-name">gpt-image-2</div>
            <div class="model-desc">智能图像生成 ¥0.99/次</div>
          </div>

          <div class="model-card" :class="{ active: selectedModel === 'nano-banana-2' }" @click="selectedModel = 'nano-banana-2'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" stroke-width="2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="model-name">Nano Banana 2</div>
            <div class="model-desc">高精度图像生成</div>
          </div>
        </div>
      </div>

      <!-- 比例选择 -->
      <div class="ratio-section">
        <div class="section-title">图片比例</div>
        <div class="ratio-grid">
          <div class="ratio-item" :class="{ active: selectedRatio === 'auto' }" @click="selectedRatio = 'auto'">
            <span>自动</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedRatio === '1:1' }" @click="selectedRatio = '1:1'">
            <div class="ratio-icon square"></div>
            <span>1:1</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedRatio === '16:9' }" @click="selectedRatio = '16:9'">
            <div class="ratio-icon wide"></div>
            <span>16:9</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedRatio === '9:16' }" @click="selectedRatio = '9:16'">
            <div class="ratio-icon portrait"></div>
            <span>9:16</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedRatio === '4:3' }" @click="selectedRatio = '4:3'">
            <div class="ratio-icon standard"></div>
            <span>4:3</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedRatio === '3:4' }" @click="selectedRatio = '3:4'">
            <div class="ratio-icon standard-flip"></div>
            <span>3:4</span>
          </div>
        </div>
      </div>

      <!-- 生成按钮 -->
      <div class="action-section">
        <button class="generate-btn" @click="generateImage" :disabled="generating">
          <span>{{ generating ? '生成中...' : '立即生成' }}</span>
          <span v-if="!generating" class="cost">¥{{getModelPrice()}}/次</span>
        </button>
      </div>
    </div>

    <!-- 视频生成内容 -->
    <div class="content-section" v-if="currentTab === 'video'">
      <!-- 上传参考图/视频 -->
      <div class="upload-section">
        <div class="section-title">上传参考素材</div>
        <div class="upload-grid">
          <div 
            class="upload-item" 
            v-for="(media, index) in videoReferences" 
            :key="index"
            @click="removeVideoReference(index)"
          >
            <img v-if="media.type === 'image'" :src="media.url" class="uploaded-image" />
            <video v-else :src="media.url" class="uploaded-video" controls></video>
            <div class="remove-btn">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
          
          <div class="upload-box" v-if="videoReferences.length < 3" @click="triggerVideoUpload">
            <input
              type="file"
              ref="videoFileInput"
              accept="image/*,video/*"
              @change="handleVideoFileSelect"
              style="display: none"
            />
            <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- 视频描述 -->
      <div class="prompt-section">
        <div class="section-title">视频描述</div>
        <textarea 
          class="prompt-input" 
          v-model="videoPrompt" 
          placeholder="描述你想要生成的视频内容，例如：一个女孩在海边奔跑，海浪拍打着沙滩，夕阳西下..."
          rows="4"
        ></textarea>
      </div>

      <!-- 视频模型选择 -->
      <div class="model-section">
        <div class="section-title">选择模型</div>
        <div class="model-grid">
          <div class="model-card" :class="{ active: selectedVideoModel === 'sora_video2' }" @click="selectedVideoModel = 'sora_video2'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="5,3 19,12 5,21" fill="currentColor"/>
              </svg>
            </div>
            <div class="model-name">Sora 2 竖屏</div>
            <div class="model-desc">704×1280, 10秒</div>
          </div>

          <div class="model-card" :class="{ active: selectedVideoModel === 'sora_video2-landscape' }" @click="selectedVideoModel = 'sora_video2-landscape'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polygon points="5,3 19,12 5,21" fill="currentColor"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="model-name">Sora 2 横屏</div>
            <div class="model-desc">1280×704, 10秒</div>
          </div>

          <div class="model-card" :class="{ active: selectedVideoModel === 'sora_video2-15s' }" @click="selectedVideoModel = 'sora_video2-15s'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="2"/>
                <polygon points="10,10 10,14 14,12" fill="currentColor"/>
              </svg>
            </div>
            <div class="model-name">竖屏 15秒</div>
            <div class="model-desc">704×1280, 15秒</div>
          </div>

          <div class="model-card" :class="{ active: selectedVideoModel === 'sora_video2-landscape-15s' }" @click="selectedVideoModel = 'sora_video2-landscape-15s'">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="7" width="20" height="10" rx="2" stroke="currentColor" stroke-width="2"/>
                <polygon points="10,10 10,14 14,12" fill="currentColor"/>
              </svg>
            </div>
            <div class="model-name">横屏 15秒</div>
            <div class="model-desc">1280×704, 15秒</div>
          </div>
        </div>
      </div>

      <!-- 视频风格选择 -->
      <div class="ratio-section">
        <div class="section-title">视频风格（可选）</div>
        <div class="ratio-grid">
          <div class="ratio-item" :class="{ active: selectedVideoStyle === '' }" @click="selectedVideoStyle = ''">
            <span>默认</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedVideoStyle === 'anime' }" @click="selectedVideoStyle = 'anime'">
            <span>动漫</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedVideoStyle === 'comic' }" @click="selectedVideoStyle = 'comic'">
            <span>漫画</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedVideoStyle === 'nostalgic' }" @click="selectedVideoStyle = 'nostalgic'">
            <span>复古</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedVideoStyle === 'news' }" @click="selectedVideoStyle = 'news'">
            <span>新闻</span>
          </div>
          <div class="ratio-item" :class="{ active: selectedVideoStyle === 'selfie' }" @click="selectedVideoStyle = 'selfie'">
            <span>自拍</span>
          </div>
        </div>
      </div>

      <!-- Storyboard 选项 -->
      <div class="model-section">
        <div class="section-title">高级选项</div>
        <div class="model-grid">
          <div class="model-card" :class="{ active: useStoryboard }" @click="useStoryboard = !useStoryboard">
            <div class="model-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
                <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="model-name">故事板</div>
            <div class="model-desc">更精细的细节控制</div>
          </div>
        </div>
      </div>

      <!-- 生成进度 -->
      <div class="progress-section" v-if="videoGenerating && videoProgress">
        <div class="progress-content" v-html="videoProgress"></div>
      </div>

      <!-- 生成按钮 -->
      <div class="action-section">
        <button class="generate-btn" @click="generateVideo" :disabled="videoGenerating">
          <span>{{ videoGenerating ? '生成中...' : '立即生成' }}</span>
          <span v-if="!videoGenerating" class="cost">¥7.99/次</span>
        </button>
      </div>

    </div>

  </div>
</template>

<script>
export default {
  name: 'ImageEdit',
  data() {
    return {
      currentTab: 'image',
      uploadedImages: [],
      uploadedImageFiles: [], // 保存原始文件对象
      prompt: '',
      selectedModel: 'nano',
      selectedRatio: 'auto',
      generating: false,

      // 视频生成相关
      videoReferences: [],
      videoPrompt: '',
      selectedVideoModel: 'sora_video2',
      selectedVideoStyle: '',
      useStoryboard: false,
      videoGenerating: false,
      videoProgress: ''
    };
  },
  mounted() {
    this.loadFromQuery();
  },
  methods: {
    async loadFromQuery() {
      const query = this.$route.query;
      if (query.type === 'continue' || query.type === 'copy') {
        if (query.prompt) {
          this.prompt = query.prompt;
        }
        
        // 处理多个原图（优先使用 originalImages）
        if (query.originalImages) {
          // 按逗号分隔获取所有原图路径
          const originalImageList = query.originalImages.split(',').map(path => path.trim()).filter(path => path);
          console.log('接收到多个原图:', originalImageList);
          
          // 设置显示的图片列表
          this.uploadedImages = [...originalImageList];
          
          // 将所有原图转换为 File 对象
          try {
            const filePromises = originalImageList.map((url, index) => 
              this.urlToFile(url, `reference_${index + 1}.jpg`)
            );
            this.uploadedImageFiles = await Promise.all(filePromises);
            console.log('成功转换', this.uploadedImageFiles.length, '个原图为文件对象');
          } catch (err) {
            console.error('转换参考图为文件失败:', err);
          }
        } else if (query.hasOriginal && query.image) {
          // 兼容旧的单图模式：只有当 hasOriginal 为 true 且没有 originalImages 时，才使用 image 作为参考图
          this.uploadedImages = [query.image];
          try {
            const file = await this.urlToFile(query.image, 'reference.jpg');
            this.uploadedImageFiles = [file];
          } catch (err) {
            console.error('转换参考图为文件失败:', err);
          }
        }
        // 注意：如果 originalImages 存在，则忽略 query.image（那是生成图，不是参考图）
        
        // 设置模型
        if (query.model) {
          this.selectedModel = query.model;
          console.log('从示例设置模型:', query.model);
        }
        this.$router.replace({ path: '/image-edit' });
      }
    },

    // 将图片 URL 转换为 File 对象
    async urlToFile(url, filename) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
        return file;
      } catch (err) {
        console.error('urlToFile失败:', err);
        throw err;
      }
    },
    
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length) {
        // 计算还能上传多少张
        const remainingSlots = 3 - this.uploadedImages.length;
        if (remainingSlots <= 0) {
          alert('最多只能上传3张参考图');
          return;
        }

        // 只处理剩余槽位数量的文件
        const filesToProcess = Math.min(files.length, remainingSlots);

        for (let i = 0; i < filesToProcess; i++) {
          const file = files[i];
          // 保存原始文件对象
          this.uploadedImageFiles.push(file);

          // 读取预览图（使用闭包保持索引对应关系）
          const reader = new FileReader();
          reader.onload = ((uploadedFile) => {
            return (e) => {
              this.uploadedImages.push(e.target.result);
            };
          })(file);
          reader.readAsDataURL(file);
        }

        // 如果选择的文件超过剩余槽位，提示用户
        if (files.length > remainingSlots) {
          alert(`只上传了前${remainingSlots}张图片，最多支持3张参考图`);
        }
      }
    },

    removeImage(index) {
      this.uploadedImages.splice(index, 1);
      this.uploadedImageFiles.splice(index, 1);
    },
    
    // 视频生成相关方法
    triggerVideoUpload() {
      this.$refs.videoFileInput.click();
    },
    
    handleVideoFileSelect(event) {
      const files = event.target.files;
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const reader = new FileReader();
          reader.onload = (e) => {
            if (this.videoReferences.length < 3) {
              this.videoReferences.push({
                type: file.type.startsWith('video/') ? 'video' : 'image',
                url: e.target.result
              });
            }
          };
          reader.readAsDataURL(file);
        }
      }
    },
    
    removeVideoReference(index) {
      this.videoReferences.splice(index, 1);
    },

    // 获取模型价格
    getModelPrice() {
      const prices = {
        'nano': 2.99,
        'nano-pro': 4.99,
        'gpt-image-2': 0.99,
        'nano-banana-2': 3.99
      };
      return prices[this.selectedModel] || 2.99;
    },
    
    async generateImage() {
      if (!this.prompt.trim()) {
        alert('请输入画面描述');
        return;
      }

      this.generating = true;

      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          alert('请先登录');
          return;
        }

        const user = JSON.parse(userStr);

        // 从 localStorage 获取 agentId
        const agentId = localStorage.getItem('agentId');

        // 准备订单数据
        const orderData = {
          userId: user.id,
          prompt: this.prompt,
          model: this.selectedModel,
          ratio: this.selectedRatio,
          amount: this.getModelPrice(), // 根据模型动态定价
          description: 'AI图片生成',
          type: 'image',
          hasImage: this.uploadedImages.length > 0,
          agentId: agentId || null
        };

        // 获取保存的图片文件对象
        const imageFiles = this.uploadedImageFiles;
        console.log('图片文件数量:', imageFiles.length);
        if (imageFiles.length > 0) {
          console.log('第一个文件:', imageFiles[0].name, imageFiles[0].type, imageFiles[0].size);
        }

        // 创建订单（支持文件上传，直接保存到正式目录）
        const { apiCreateImageOrder, callWechatPay } = await import('@/utils/api');
        const response = await apiCreateImageOrder(orderData, imageFiles);

        if (!response.success) {
          throw new Error(response.error || '创建订单失败');
        }

        // 调试模式直接返回成功
        if (!response.payParams) {
          this.$router.push('/my-works');
          return;
        }

        // 正式模式调起微信支付
        await callWechatPay(response.payParams);

        // 支付成功，跳转到我的作品页面
        this.$router.push('/my-works');

      } catch (error) {
        console.error('支付失败:', error);
        alert('支付失败：' + error.message);
      } finally {
        this.generating = false;
      }
    },
    
    async generateVideo() {
      if (!this.videoPrompt.trim()) {
        alert('请输入视频描述');
        return;
      }

      this.videoGenerating = true;
      this.videoProgress = '';

      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          alert('请先登录');
          return;
        }

        const user = JSON.parse(userStr);

        // 从 localStorage 获取 agentId
        const agentId = localStorage.getItem('agentId');

        // 准备订单数据
        const orderData = {
          userId: user.id,
          prompt: this.videoPrompt,
          model: this.selectedVideoModel,
          style: this.selectedVideoStyle,
          storyboard: this.useStoryboard,
          amount: 0.01, // 视频生成价格
          description: 'AI视频生成',
          type: 'video',
          hasReference: this.videoReferences.length > 0,
          agentId: agentId || null
        };

        // 获取视频参考素材文件
        const videoFiles = this.videoReferenceFiles || [];

        // 创建订单（支持文件上传）
        const { apiCreateImageOrder, callWechatPay } = await import('@/utils/api');
        const response = await apiCreateImageOrder(orderData, videoFiles);

        if (!response.success) {
          throw new Error(response.error || '创建订单失败');
        }

        // 调试模式直接返回成功
        if (!response.payParams) {
          this.$router.push('/my-works');
          return;
        }

        // 正式模式调起微信支付
        await callWechatPay(response.payParams);

        // 支付成功，跳转到我的作品页面
        this.$router.push('/my-works');

      } catch (error) {
        console.error('支付失败:', error);
        alert('支付失败：' + error.message);
      } finally {
        this.videoGenerating = false;
      }
    }
  }
};
</script>

<style scoped>
.image-edit-page {
  min-height: 100vh;
  background: #fff9f0;
  padding-bottom: 70px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  width: 36px;
  height: 36px;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
}

.action-btn svg {
  width: 20px;
  height: 20px;
}

.tab-container {
  display: flex;
  gap: 8px;
  padding: 0 20px 16px;
}

.tab-btn {
  flex: 1;
  padding: 10px 20px;
  background: #ffe8c0;
  border: none;
  border-radius: 24px;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn.active {
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  font-weight: 600;
}

.main-content {
  padding: 0 20px;
}

.section {
  margin-bottom: 24px;
}

.section-header {
  margin-bottom: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.section-subtitle {
  font-size: 13px;
  color: #999;
}

.upload-section {
  padding: 0 20px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.upload-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.upload-box {
  width: 120px;
  height: 120px;
  background: #f0f0f0;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-box:active {
  transform: scale(0.95);
}

.upload-box:hover {
  background: #e5e5e5;
}

/* 视频生成上传样式 */
.upload-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 16px;
  overflow: hidden;
  background: #f0f0f0;
  cursor: pointer;
}

.upload-item img,
.upload-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-item .remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  padding: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.upload-item:hover .remove-btn {
  opacity: 1;
}

.upload-item .remove-btn svg {
  width: 16px;
  height: 16px;
  stroke: white;
}

.upload-icon {
  width: 40px;
  height: 40px;
  stroke: #999;
}

.uploaded-image-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 16px;
  overflow: hidden;
}

.uploaded-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-img-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 28px;
  height: 28px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  padding: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.uploaded-image-wrapper:hover .remove-img-btn {
  opacity: 1;
}

.remove-img-btn svg {
  width: 16px;
  height: 16px;
  stroke: white;
}

.textarea-container {
  position: relative;
}

textarea {
  width: 100%;
  padding: 16px;
  background: #f5f5f5;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  color: #333;
  resize: none;
  outline: none;
  box-sizing: border-box;
}

.char-count {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #999;
}

.model-section {
  padding: 0 20px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.model-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.model-card {
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.model-card.active {
  background: #fff;
  border-color: #ff9500;
}

.model-card.active .model-name {
  color: #ff9500;
}

.model-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.model-icon svg {
  width: 40px;
  height: 40px;
  stroke: #666;
  fill: none;
}

.model-card.active .model-icon svg {
  stroke: #ff9500;
  fill: #ff9500;
}

.model-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.model-desc {
  font-size: 12px;
  color: #999;
  text-align: center;
}

.tip-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: #fff9f0;
}

.tip-link svg {
  width: 18px;
  height: 18px;
  stroke: #ff9500;
}

.tip-link span {
  font-size: 13px;
  color: #999;
}

.link-btn {
  padding: 4px 12px;
  background: transparent;
  border: 1px solid #ff9500;
  border-radius: 16px;
  font-size: 13px;
  color: #ff9500;
  cursor: pointer;
}

.ratio-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.ratio-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  background: #f5f5f5;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ratio-item.active {
  background: #fff;
  border-color: #ff9500;
}

.ratio-item:not(.active):hover {
  background: #f0f0f0;
}

.ratio-item span {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.ratio-item.active span {
  color: #ff9500;
}

.ratio-icon {
  width: 32px;
  height: 32px;
  border: 2px solid #ccc;
  border-radius: 4px;
}

.ratio-item.active .ratio-icon {
  border-color: #ff9500;
}

.ratio-icon.auto {
  width: 20px;
  height: 28px;
}

.ratio-icon.square {
  width: 28px;
  height: 28px;
}

.ratio-icon.vertical {
  width: 20px;
  height: 28px;
}

.ratio-icon.horizontal {
  width: 28px;
  height: 20px;
}

.ratio-icon.wide {
  width: 32px;
  height: 18px;
}

.ratio-icon.tall {
  width: 18px;
  height: 32px;
}

.ratio-icon.ultra-wide {
  width: 36px;
  height: 16px;
}

.result-section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.result-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-image {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.result-actions {
  display: flex;
  gap: 12px;
}

.download-btn,
.regenerate-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.download-btn {
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
}

.regenerate-btn {
  background: #f5f5f5;
  color: #333;
}

.download-btn:active,
.regenerate-btn:active {
  transform: scale(0.98);
}

.download-btn svg,
.regenerate-btn svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
}

.generate-btn {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  border: none;
  border-radius: 28px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(255, 149, 0, 0.3);
  transition: all 0.2s ease;
}

.generate-btn:active {
  transform: scale(0.98);
}

.generate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cost {
  font-size: 14px;
  opacity: 0.9;
}

.disclaimer {
  text-align: center;
  font-size: 12px;
  color: #999;
  margin-top: 16px;
  padding-bottom: 20px;
}

/* 视频生成进度 */
.progress-section {
  padding: 0 20px;
  margin-bottom: 20px;
}

.progress-content {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 16px;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
}

.progress-content :deep(a) {
  color: #ff9500;
  text-decoration: none;
}

/* 视频结果 */
.result-video-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-video {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.video-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fff3e0;
  border-radius: 8px;
  color: #ff9500;
  font-size: 13px;
}

.video-warning svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
</style>